import {
  isCancelledError,
  QueryClient,
  type DefaultError,
  type EnsureQueryDataOptions,
  type QueryKey,
} from '@tanstack/react-query'

import { ApiClientError } from '@ems/api-client/client'

/**
 * Query defaults, chosen for an operator console rather than a content site.
 *
 * The one that matters is the retry policy: a 403 or a 404 from the module guard is a
 * *decision*, not a blip, and retrying it three times turns an instant "this module is not on
 * your plan" into a two-second spinner that ends the same way. Only genuine transport failures
 * and 5xx are worth trying again.
 */
export class LoaderQueryClient extends QueryClient {
  override async ensureQueryData<
    TQueryFnData,
    TError = DefaultError,
    TData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey,
  >(options: EnsureQueryDataOptions<TQueryFnData, TError, TData, TQueryKey>): Promise<TData> {
    try {
      return await super.ensureQueryData(options)
    } catch (error) {
      if (!isCancelledError(error)) throw error
      return super.ensureQueryData(options)
    }
  }
}

export function createQueryClient(): QueryClient {
  return new LoaderQueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: (failureCount, error) => {
          if (error instanceof ApiClientError && error.status < 500) return false
          return failureCount < 2
        },
        refetchOnWindowFocus: false,
      },
      mutations: {
        // Never. A retried mutation is a second invoice, a second allocation, a second badge.
        retry: false,
      },
    },
  })
}
